import { Button, Flex, Select } from "@mantine/core";
import { NumOfBsd, NumOfGroup, PAGE_SIZE } from "CommonData";
import SRD_DataTable from "components/DataTable/index";
import { getBsdList, getChList } from "pages/Admin/Device/apis/adminDeviceApis";
import { currentBsdState } from "pages/Login/atom/currentBsdState";
import { userGroupState } from "pages/Login/atom/userGroupState";
import { userState } from "pages/Login/atom/userState";
import {
  currentBsdListState,
  registedAlarmSetListState,
  smsItemUserState,
} from "pages/atom/globalState";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { deleteAlarmItem, getAlarmItemList } from "../apis/itemSettingApis";

function ItemSettingTable() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [noOfGroup] = useState(NumOfGroup);
  const [user, setUser] = useRecoilState(userState);
  const [userGroup] = useRecoilState(userGroupState);
  const [alarmSetListState, setAlarmSetListState] = useRecoilState(
    registedAlarmSetListState
  );
  const [itemUserState, setItemUserState] = useRecoilState(smsItemUserState);
  const [bsdState, setBsdState] = useRecoilState(currentBsdState);
  const [bsdListState, setBsdListState] = useRecoilState(currentBsdListState);
  const [chList, setChList] = useState([]);

  useEffect(() => {
    if (userGroup) {
      getBsdList(NumOfBsd, page, userGroup).then((resList) => {
        const filteredList = resList.filter(
          (item) => item.group_id === userGroup
        );
        const formattedBsdList = filteredList.map((item) => ({
          value: item.bsd_id,
          label: item.bsd_nm,
        }));
        setBsdListState(formattedBsdList);
      });
    }
  }, [userGroup, NumOfBsd, page]);

  useEffect(() => {
    const fetchDataAndUpdateState = async () => {
      if (bsdState === "") return;

      try {
        const chListRes = await getChList(bsdState);
        const formattedChList = processChList(chListRes, bsdState);
        setChList(formattedChList);

        await new Promise(resolve => setTimeout(resolve, 500));  // 임시로 0.5초 지연시킴 for  alarm_status_ 렌더링
        const alarmItemRes = await getAlarmItemList(bsdState, pageSize, page);
        // TN_ALARM_ITEM의 모든 항목을 표시 (채널 필터링 제거)
        setAlarmSetListState(alarmItemRes || []);

        logResults(formattedChList, alarmItemRes, alarmItemRes);
      } catch (error) {
        console.error("데이터 로드 중 오류 발생:", error);
        setChList([]);
        setAlarmSetListState([]);
      }
    };

    fetchDataAndUpdateState();
  }, [itemUserState, bsdState, page, pageSize]);

  const processChList = (res, bsdState) => {
    if (!Array.isArray(res)) return [];
    
    return res
      .filter(item => item.bsd_id === bsdState)
      .map(item => ({
        value: item.chnnl_no,
        label: item.chnnl_nm,
      }));
  };

  const filterAlarmSetList = (resList, formattedChList) => {
    if (!Array.isArray(resList)) return { filteredList: [], deletedItems: [] };

    const validChannels = new Set(formattedChList.map(ch => ch.value));
    const filteredList = resList.filter(item => validChannels.has(item.chnnl_no));
    const deletedItems = resList.filter(item => !validChannels.has(item.chnnl_no));

    return { filteredList, deletedItems };
  };

  const deleteRemovedAlarmItems = async (deletedItems) => {
    for (const item of deletedItems) {
      try {
        await deleteAlarmItem(item.bsd_sttus_id);
        console.log(`알람 항목 삭제 완료: ${item.bsd_sttus_id}`);
      } catch (error) {
        console.error(`알람 항목 삭제 실패: ${item.bsd_sttus_id}`, error);
      }
    }
  };

  const logResults = (formattedChList, originalList, filteredList) => {
    console.log("formattedChList:", formattedChList);
    
    if (formattedChList.length === 0) {
      console.log("선택할 채널 항목이 없습니다.");
    }

    if (Array.isArray(originalList) && filteredList.length < originalList.length) {
      console.log(`${originalList.length - filteredList.length}개의 항목이 chList에 없어 제거되었습니다.`);
    }
  };

  const columns = [
    {
      accessor: "index",
      title: "번호",
      textAlignment: "center",
      width: 30,
      render: (record) => alarmSetListState.indexOf(record) + 1,
    },
    {
      accessor: "chnnl_nm",
      title: "알람 항목",
      textAlignment: "center",
      width: 150,
      cellsStyle: { color: "blue", textDecoration: "underline" },
      onClick: (e) => {
        setItemUserState(e.record);
        navigate(`/sms/item/${e.record.chnnl_nm}`, {
          state: {
            id: e.record.id,
            bsd_sttus_id: e.record.bsd_sttus_id,
            bsd_id: e.record.bsd_id,
            chnnl_no: e.record.chnnl_no,
            sms_ment: e.record.sms_ment,
            operator: e.record.operator,
            set_value: e.record.set_value,
            tx_count_: e.record.tx_count_,
            tx_period: e.record.tx_period,
            alarm_yn: e.record.alarm_yn,
          },
        });
      },
    },
    { accessor: "sms_ment", title: "알람메세지", textAlignment: "center" },
    { accessor: "operator", title: "조건", textAlignment: "center" },
    { accessor: "set_value", title: "설정값", textAlignment: "center" },
    { accessor: "tx_count_", title: "반복 횟수", textAlignment: "center" },
    {
      accessor: "tx_period",
      title: "재전송 주기(분)",
      textAlignment: "center",
    },
    { accessor: "alarm_yn", title: "알람 사용 여부", textAlignment: "center" },
    { accessor: "alarm_status_", title: "알람 상태", textAlignment: "center" },
  ];

  const SearchBar = () => (
    <Flex gap={15} mb={10} align="center">
      <Select
        placeholder="분사 장치 선택"
        data={bsdListState ?? []}
        value={bsdState || null}
        onChange={setBsdState}
      />
      <Button onClick={() => navigate("/sms/item/add")}>등록</Button>
    </Flex>
  );

  return (
    <SRD_DataTable
      searchBar={<SearchBar />}
      data={Array.isArray(alarmSetListState) ? alarmSetListState : []}
      columns={columns}
      page={page}
      setPage={setPage}
    />
  );
}

export default ItemSettingTable;
